import React, { useEffect, useMemo, useRef, useState } from 'react';

import ContentTable from './ContentTable';
import TableCellRichEditor from './TableCellRichEditor';
import {
  getColumnLayout,
  getTableCellEditorHtml,
  getTableCellOwnership,
  getTableCellStyle,
  mergeTableCell,
  mergeTableCellRange,
  normalizeTableShape,
  styleTableCell,
  unmergeTableCell,
  updateTableCellContent,
  validateColumnLayout,
} from './contentLayout';


const draftFromTable = (table) => {
  const configured = table?.column_widths;
  if (
    configured &&
    typeof configured === 'object' &&
    typeof configured.unit === 'string' &&
    Array.isArray(configured.values)
  ) {
    return {
      unit: configured.unit,
      values: configured.values.map(String),
    };
  }
  const layout = getColumnLayout(table);
  return {
    unit: layout.unit,
    values: layout.values.map(String),
  };
};

const TableEditor = ({ table, onChange, onValidityChange }) => {
  const shaped = useMemo(() => normalizeTableShape(table), [table]);
  const [draft, setDraft] = useState(() => draftFromTable(table));
  const [error, setError] = useState('');
  const [mergeError, setMergeError] = useState('');
  const [activeCell, setActiveCell] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const validityCallbackRef = useRef(onValidityChange);
  const tableSignature = useMemo(
    () => JSON.stringify(table || {}),
    [table]
  );
  const externalTable = useMemo(
    () => JSON.parse(tableSignature),
    [tableSignature]
  );

  useEffect(() => {
    validityCallbackRef.current = onValidityChange;
  }, [onValidityChange]);

  useEffect(() => {
    const nextDraft = draftFromTable(externalTable);
    const result = validateColumnLayout(shaped.columns, nextDraft);
    setDraft(nextDraft);
    setError(result.error);
    validityCallbackRef.current?.(result.valid);
  }, [externalTable, shaped.columns]);

  const commitIfValid = (nextDraft) => {
    const result = validateColumnLayout(shaped.columns, nextDraft);
    setError(result.error);
    onValidityChange?.(result.valid);
    if (result.valid) {
      onChange?.({
        ...shaped,
        column_widths: result.value,
      });
    }
  };

  const changeUnit = (unit) => {
    const values =
      unit === 'percent'
        ? getColumnLayout({ columns: shaped.columns }).values
        : Array.from({ length: shaped.columns }, () => 240);
    const nextDraft = {
      unit,
      values: values.map(String),
    };
    setDraft(nextDraft);
    commitIfValid(nextDraft);
  };

  const changeWidth = (index, value) => {
    const values = [...draft.values];
    values[index] = value;
    const nextDraft = { ...draft, values };
    setDraft(nextDraft);
    commitIfValid(nextDraft);
  };

  const previewTable = {
    ...shaped,
    column_widths: {
      unit: draft.unit,
      values: draft.values.map((value) => Number(value)),
    },
  };
  const ownership = useMemo(
    () => getTableCellOwnership(shaped),
    [shaped]
  );
  const activeOwner = activeCell
    ? ownership.get(`${activeCell.row}:${activeCell.column}`) || {
        ...activeCell,
        rowspan: 1,
        colspan: 1,
      }
    : null;
  const selectedRange = activeCell
    ? {
        startRow: activeCell.row,
        startColumn: activeCell.column,
        endRow: rangeEnd?.row ?? activeCell.row,
        endColumn: rangeEnd?.column ?? activeCell.column,
      }
    : null;
  const activeStyle = activeOwner
    ? getTableCellStyle(shaped, activeOwner)
    : null;
  const canMergeRight = Boolean(
    activeOwner &&
      activeOwner.column + activeOwner.colspan < shaped.columns
  );
  const canMergeDown = Boolean(
    activeOwner && activeOwner.row + activeOwner.rowspan < shaped.rows
  );
  const canUnmerge = Boolean(
    activeCell && ownership.has(`${activeCell.row}:${activeCell.column}`)
  );
  const canMergeSelection = Boolean(
    selectedRange &&
      (selectedRange.startRow !== selectedRange.endRow ||
        selectedRange.startColumn !== selectedRange.endColumn)
  );

  const mergeSelectedCell = (direction) => {
    const result = mergeTableCell(shaped, {
      ...activeCell,
      direction,
    });
    setMergeError(result.error);
    if (!result.error) onChange?.(result.table);
  };

  const unmergeSelectedCell = () => {
    setMergeError('');
    onChange?.(unmergeTableCell(shaped, activeCell));
  };

  const mergeSelection = () => {
    const result = mergeTableCellRange(shaped, selectedRange);
    setMergeError(result.error);
    if (!result.error) onChange?.(result.table);
  };

  const editActiveCell = (html) => {
    if (!activeOwner) return;
    onChange?.(updateTableCellContent(shaped, activeOwner, html));
  };

  const applyStyleToSelection = (patch) => {
    if (!selectedRange) return;
    let nextTable = shaped;
    const top = Math.min(selectedRange.startRow, selectedRange.endRow);
    const bottom = Math.max(selectedRange.startRow, selectedRange.endRow);
    const left = Math.min(selectedRange.startColumn, selectedRange.endColumn);
    const right = Math.max(selectedRange.startColumn, selectedRange.endColumn);
    const visibleOwners = new Map();
    for (let row = top; row <= bottom; row += 1) {
      for (let column = left; column <= right; column += 1) {
        const owner = ownership.get(`${row}:${column}`) || { row, column };
        visibleOwners.set(`${owner.row}:${owner.column}`, owner);
      }
    }
    visibleOwners.forEach((owner) => {
      nextTable = styleTableCell(nextTable, owner, patch);
    });
    onChange?.(nextTable);
  };

  return (
    <div className="book-table-editor">
      <div className="book-table-width-controls">
        <label className="book-table-width-field">
          <span>Unité</span>
          <select
            aria-label="Unité des largeurs"
            value={draft.unit}
            onChange={(event) => changeUnit(event.target.value)}
          >
            <option value="percent">Pourcentage (%)</option>
            <option value="px">Pixels (px)</option>
          </select>
        </label>

        {draft.values.map((value, index) => (
          <label className="book-table-width-field" key={index}>
            <span>Colonne {index + 1}</span>
            <span className="book-table-width-input">
              <input
                aria-label={`Largeur colonne ${index + 1}`}
                type="number"
                min={draft.unit === 'percent' ? '0.01' : '40'}
                max={draft.unit === 'percent' ? '100' : '2000'}
                step={draft.unit === 'percent' ? '0.01' : '1'}
                value={value}
                onChange={(event) => changeWidth(index, event.target.value)}
              />
              <span>{draft.unit === 'percent' ? '%' : 'px'}</span>
            </span>
          </label>
        ))}
      </div>

      {error && (
        <p className="book-table-width-error" role="alert">
          {error}
        </p>
      )}

      {activeOwner && (
        <div className="book-table-cell-controls" aria-label="Mise en forme de la cellule">
          <div className="book-table-cell-content-controls">
            <span>Contenu de la cellule</span>
            <TableCellRichEditor
              html={getTableCellEditorHtml(shaped, activeOwner)}
              onChange={editActiveCell}
            />
          </div>
          <div className="book-table-cell-direction book-table-cell-alignment">
            {[
              ['left', 'Aligner à gauche', 'Gauche'],
              ['center', 'Aligner au centre', 'Centre'],
              ['right', 'Aligner à droite', 'Droite'],
              ['justify', 'Justifier le texte', 'Justifié'],
            ].map(([alignment, ariaLabel, label]) => (
              <button
                key={alignment}
                type="button"
                aria-label={ariaLabel}
                aria-pressed={
                  activeStyle?.text_align === alignment ||
                  (alignment === 'left' && !activeStyle?.text_align)
                }
                onClick={() =>
                  applyStyleToSelection({ text_align: alignment })
                }
              >
                {label}
              </button>
            ))}
          </div>
          <div className="book-table-cell-direction">
            <button
              type="button"
              aria-label="Texte horizontal"
              aria-pressed={!activeStyle?.text_direction}
              onClick={() => applyStyleToSelection({ text_direction: null })}
            >
              Horizontal
            </button>
            <button
              type="button"
              aria-label="Texte vertical"
              aria-pressed={activeStyle?.text_direction === 'vertical-up'}
              onClick={() =>
                applyStyleToSelection({ text_direction: 'vertical-up' })
              }
            >
              Vertical bas → haut
            </button>
          </div>
          <label className="book-table-cell-color">
            <span>Couleur de fond</span>
            <input
              type="color"
              aria-label="Couleur de fond de la cellule"
              value={activeStyle?.background_color || '#ffffff'}
              onChange={(event) =>
                applyStyleToSelection({ background_color: event.target.value })
              }
            />
          </label>
          <div className="book-table-color-palette" aria-label="Couleurs rapides">
            {[
              ['Vert', '#4CAF32'],
              ['Jaune', '#FDE68A'],
              ['Gris', '#E5E7EB'],
            ].map(([label, color]) => (
              <button
                key={color}
                type="button"
                aria-label={`Fond ${label.toLowerCase()}`}
                style={{ backgroundColor: color }}
                onClick={() =>
                  applyStyleToSelection({ background_color: color })
                }
              />
            ))}
            <button
              type="button"
              aria-label="Sans couleur"
              onClick={() =>
                applyStyleToSelection({ background_color: null })
              }
            >
              Sans couleur
            </button>
          </div>
        </div>
      )}

      <div className="book-table-merge-controls" aria-label="Fusion des cellules">
        <button
          type="button"
          disabled={!canMergeSelection}
          onClick={mergeSelection}
        >
          Fusionner la sélection
        </button>
        <button
          type="button"
          disabled={!canMergeRight}
          onClick={() => mergeSelectedCell('right')}
        >
          Fusionner à droite
        </button>
        <button
          type="button"
          disabled={!canMergeDown}
          onClick={() => mergeSelectedCell('down')}
        >
          Fusionner en bas
        </button>
        <button
          type="button"
          disabled={!canUnmerge}
          onClick={unmergeSelectedCell}
        >
          Défusionner
        </button>
      </div>

      {mergeError && (
        <p className="book-table-width-error" role="alert">
          {mergeError}
        </p>
      )}

      <div className="book-table-preview" aria-label="Aperçu du tableau">
        <ContentTable
          table={previewTable}
          activeCell={activeCell}
          selectedRange={selectedRange}
          onCellClick={(cell, event) => {
            if (event?.shiftKey && activeCell) {
              setRangeEnd(cell);
            } else {
              setActiveCell(cell);
              setRangeEnd(cell);
            }
            setMergeError('');
          }}
        />
      </div>
    </div>
  );
};

export default TableEditor;
